import { Transmit } from '@adonisjs/transmit-client'
import { generateUUID } from '@thecompaniesapi/string-helpers'
import promiseRetry from 'promise-retry'
import type { Subscription } from '@adonisjs/transmit-client'
import { useRuntimeConfig } from '#imports'

interface HttpClientOptions {
  baseUrl: string
  token: string
  uid: string
}

class HttpClient {
  #options: HttpClientOptions

  constructor(options: HttpClientOptions) {
    this.#options = options
  }

  createRequest(path: string, body: any): Request {
    return new Request(`${this.#options.baseUrl}${path}`, {
      body: JSON.stringify({
        ...body,
        uid: this.#options.uid,
      }),
      credentials: 'include',
      headers: {
        'Authorization': this.#options.token ? `Bearer ${this.#options.token}` : '',
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })
  }

  async send(request: any): Promise<Response> {
    return await fetch(request)
  }
}

export default defineNuxtPlugin(async () => {
  const globalSubscription = shallowRef<Subscription>()
  const lastEvent = ref<number>()
  const pinging = ref(true)
  const teamSubscription = shallowRef<Subscription>()
  const transmit = shallowRef<InstanceType<typeof Transmit>>()
  const userSubscription = shallowRef<Subscription>()
  const visitorSubscription = shallowRef<Subscription>()
  const listsSubscriptions = shallowReactive<Record<string, Subscription>>({})

  // Visitor id handler
  const visitorId = useLocalStorage('visitorId', '')
  const cleanVisitorId = () => {
    visitorId.value = ''
  }
  const updateVisitorId = () => {
    visitorId.value = generateUUID()
  }

  const authStore = useAuthStore()
  const runtimeConfig = useRuntimeConfig()
  const teamStore = useTeamsStore()
  const userStore = useUserStore()

  const connected = computed(() => {
    return {
      global: !!globalSubscription.value,
      team: !!teamSubscription.value,
      transmit: !!transmit.value,
      user: !!userSubscription.value,
      visitor: !!visitorSubscription.value,
    }
  })

  const userId = computed(() => {
    return userStore?.user?.id
  })

  const teamId = computed(() => {
    return teamStore?.currentTeam?.id
  })

  const initializeClient = async () => {
    try {
      if (transmit.value?.close) {
        transmit.value.close()
      }

      transmit.value = new Transmit({
        baseUrl: runtimeConfig.public.apis.socket,
        httpClientFactory: (baseUrl: string, uid: string) => new HttpClient({
          baseUrl,
          token: authStore.opaqueToken || '',
          uid,
        }) as any,
      })
    }
    catch (error) {
      console.warn('Could not initialize socket client!', error)
    }
  }

  const cleanupSubscriptions = async () => {
    // Global channel
    if (globalSubscription.value) {
      try {
        await globalSubscription.value.delete()
      }
      catch {
        console.warn('Could not disconnect from global channel')
      }
    }

    // Cleanup user subscription
    if (userSubscription.value) {
      try {
        await userSubscription.value.delete()
      }
      catch {
        console.warn('Could not disconnect from user channel')
      }
    }

    // Cleanup team subscription
    if (teamSubscription.value) {
      try {
        await teamSubscription.value.delete()
      }
      catch {
        console.warn('Could not disconnect from team channel')
      }
    }

    // Cleanup visitor subscription
    if (visitorSubscription.value) {
      try {
        await visitorSubscription.value.delete()
      }
      catch {
        console.warn('Could not disconnect from visitor channel')
      }
    }

    // Cleanup list subscriptions
    for (const channel in listsSubscriptions) {
      if (listsSubscriptions[channel]) {
        try {
          await listsSubscriptions[channel].delete()

          delete listsSubscriptions[channel]
        }
        catch {
          console.warn('Could not disconnect from list channel', channel)
        }
      }
    }
  }

  const registerSubscriptions = async () => {
    if (!transmit.value) {
      return
    }

    if (!globalSubscription.value) {
      try {
        globalSubscription.value = transmit.value.subscription('global')

        await globalSubscription.value.create()
      }
      catch (error) {
        console.warn('Could not connect to global channel', error)
      }
    }

    // User channel
    if (authStore.opaqueToken && userId.value && !userSubscription.value) {
      try {
        userSubscription.value = transmit.value.subscription(`users/${userId.value}`)
        await userSubscription.value.create()
      }
      catch (error) {
        console.warn('Could not connected to user channel', error)
      }
    }

    if (visitorId.value && !visitorSubscription.value) {
      await promiseRetry(
        async (retry) => {
          try {
            if (!transmit.value) {
              return retry(new Error('Transmit instance not found'))
            }

            // Update visitor id if not found
            if (!visitorId.value) {
              updateVisitorId()
            }

            visitorSubscription.value = transmit.value.subscription(`visitors/${visitorId.value}`)
            await visitorSubscription.value.create()
          }
          catch (error) {
            cleanVisitorId()
            retry(error)
          }
        },
        {
          factor: 2,
          retries: 3,
        },
      )
    }

    // Team channel
    if (teamId.value && !teamSubscription.value) {
      try {
        teamSubscription.value = transmit.value.subscription(`teams/${teamId.value}`)

        await teamSubscription.value.create()
      }
      catch (error) {
        console.warn('Could not connected to team channel', error)
      }
    }
  }

  watch(
    () => [userId.value, teamId.value],
    async () => {
      await cleanupSubscriptions()
      await initializeClient()
      await registerSubscriptions()
    },
  )

  await cleanupSubscriptions()
  await initializeClient()
  await registerSubscriptions()

  return {
    provide: {
      socket: {
        connected,
        globalSubscription,
        lastEvent,
        listsSubscriptions,
        pinging,
        teamSubscription,
        transmit,
        userSubscription,
        visitorSubscription,
      },
      visitor: {
        clean: cleanVisitorId,
        id: visitorId,
        update: updateVisitorId,
      },
    },
  }
})
